import React from 'react';
import { toast } from 'react-hot-toast';
import styles from './WatcherAssets.module.css';

// Custom loading spinner component
const LoadingSpinner = () => (
    <div className={styles.loading_spinner_container}>
        <div className={styles.loading_spinner}></div>
    </div>
);

const IpTrafficModal = ({ 
    isOpen, 
    onClose, 
    ipAddress, 
    watcherId 
}) => {
    const [loading, setLoading] = React.useState(false);
    const [trafficData, setTrafficData] = React.useState(null);

    const fetchTrafficData = React.useCallback(async () => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('ipAddress', ipAddress);
            if (watcherId) {
                formData.append('watcherId', watcherId);
            }

            const response = await fetch('/api/ip-traffic-details', {
                method: 'POST',
                body: formData
            });

            const data = await response.json();
            
            if (data && data.status) {
                setTrafficData(data);
            } else {
                toast.error(data.msg || 'Failed to fetch traffic data');
            }
        } catch (error) {
            console.error('Error fetching traffic data:', error);
            toast.error('Error fetching traffic data');
        } finally {
            setLoading(false);
        }
    }, [ipAddress, watcherId]);

    React.useEffect(() => {
        if (isOpen && ipAddress) {
            fetchTrafficData();
        }
    }, [isOpen, ipAddress, fetchTrafficData]);

    // Helper function to create data pairs from columns and first row
    const getAssetDetails = () => {
        if (!trafficData || !trafficData.trafficCols || !trafficData.trafficRows || trafficData.trafficRows.length === 0) {
            return null;
        }

        const details = {};
        trafficData.trafficCols.forEach((col, index) => {
            if (trafficData.trafficRows[0] && trafficData.trafficRows[0][index]) {
                details[col] = trafficData.trafficRows[0][index];
            }
        });
        return details;
    };

    if (!isOpen) return null;

    const assetDetails = getAssetDetails();

    return (
        <div className={styles.modal_overlay} onClick={onClose}>
            <div className={styles.modal_content} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modal_header}>
                    <h2>Asset Details</h2>
                    <button 
                        className={styles.modal_close_button} 
                        onClick={onClose}
                        aria-label="Close modal"
                    >
                        ×
                    </button>
                </div>

                <div className={styles.modal_body}>
                    {loading ? (
                        <LoadingSpinner />
                    ) : assetDetails ? (
                        <div className={styles.asset_details_container}>
                            <div className={styles.asset_header}>
                                <h3 className={styles.asset_ip}>{ipAddress}</h3>
                                <span className={styles.asset_status}>Active Asset</span>
                            </div>
                            
                            <div className={styles.asset_info_grid}>
                                {Object.entries(assetDetails).map(([key, value]) => (
                                    <div key={key} className={styles.asset_info_item}>
                                        <span className={styles.asset_label}>{key}:</span>
                                        <span className={styles.asset_value}>{value || 'N/A'}</span>
                                    </div>
                                ))}
                            </div>

                            {trafficData.totalRows > 1 && (
                                <div className={styles.additional_records_notice}>
                                    <p>
                                        <strong>Note:</strong> This asset has {trafficData.totalRows} total records. 
                                        Showing primary asset information above.
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className={styles.no_data_container}>
                            <h3>No Asset Information Available</h3>
                            <p>Unable to retrieve details for IP address: <strong>{ipAddress}</strong></p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default IpTrafficModal;
